import { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarPlus, Clock, Trash2, UserCheck, CalendarCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });

const DEFAULT_TIMES = ['08:30', '08:50', '09:10', '09:30', '10:00', '10:20'];

export function MeetingsPanel({ selectedIds, onBooked }) {
  const [data, setData]       = useState({ slots: [], open_count: 0, booked_count: 0 });
  const [loading, setLoading] = useState(true);
  const [date, setDate]       = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [times, setTimes]     = useState(['08:30', '08:50', '09:10']);
  const [topic, setTopic]     = useState('Direction check-in');
  const [duration, setDuration] = useState(20);
  const [busy, setBusy]       = useState(false);
  const [msg, setMsg]         = useState('');

  const load = () => axios.get(`${API}/counselor/slots`, auth())
    .then(r => setData(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const publish = async () => {
    if (!date || times.length === 0) return;
    setBusy(true); setMsg('');
    try {
      const r = await axios.post(`${API}/counselor/slots`,
        { date, times, duration_min: duration, topic }, auth());
      setMsg(`Published ${r.data.created} open ${r.data.created === 1 ? 'time' : 'times'}`);
      await load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Could not publish'); }
    finally { setBusy(false); }
  };

  const bookSelected = async () => {
    if (selectedIds.length === 0) return;
    setBusy(true); setMsg('');
    try {
      const r = await axios.post(`${API}/counselor/slots/assign`,
        { student_ids: selectedIds, topic }, auth());
      setMsg(`Booked ${r.data.booked.length} ${r.data.booked.length === 1 ? 'student' : 'students'}`);
      onBooked?.(r.data);
      await load();
    } catch (e) { setMsg(e.response?.data?.detail || 'Could not book'); }
    finally { setBusy(false); }
  };

  const removeSlot = async (id) => {
    await axios.delete(`${API}/counselor/slots/${id}`, auth()).catch(() => {});
    load();
  };

  const toggleTime = (t) => setTimes(prev =>
    prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort());

  const panel = 'rounded-2xl border border-white/10 backdrop-blur-xl';
  const bg = { background: 'rgba(13,27,46,0.72)' };

  return (
    <div className="space-y-5" data-testid="meetings-panel">
      <div className="grid gap-5 lg:grid-cols-[420px_1fr]">
        {/* Publish open times */}
        <div className={`${panel} p-6`} style={bg}>
          <div className="mb-5 flex items-center gap-2.5">
            <CalendarPlus className="h-4 w-4 text-sky-300" />
            <h3 className="font-outfit text-base font-semibold text-white">Publish open times</h3>
          </div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.13em] text-slate-300">Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="slot-date"
            className="mt-2 w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-white focus:border-sky-300/50 focus:outline-none" />

          <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.13em] text-slate-300">Times</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {DEFAULT_TIMES.map(t => (
              <button key={t} onClick={() => toggleTime(t)} data-testid={`slot-time-${t}`}
                className={`rounded-lg border px-3 py-1.5 text-xs transition-colors ${
                  times.includes(t)
                    ? 'border-sky-300/60 bg-sky-400/[0.16] text-sky-100'
                    : 'border-white/12 bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]'}`}>
                {t}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.13em] text-slate-300">Minutes</label>
              <input type="number" value={duration} min={10} max={60} data-testid="slot-duration"
                onChange={e => { setDuration(Number(e.target.value)); setTimes([]); }}
                className="mt-2 w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-white focus:outline-none" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.13em] text-slate-300">Topic</label>
              <input value={topic} onChange={e => setTopic(e.target.value)} data-testid="slot-topic"
                className="mt-2 w-full rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2.5 text-sm text-white focus:outline-none" />
            </div>
          </div>

          <button onClick={publish} disabled={busy} data-testid="publish-slots-btn"
            className="mt-5 w-full rounded-full border border-sky-300/40 bg-sky-400/[0.16] px-4 py-2.5
              text-sm font-semibold text-sky-100 hover:bg-sky-400/[0.26] disabled:opacity-40 transition-colors">
            {busy ? 'Working…' : `Publish ${times.length} open times`}
          </button>

          <button onClick={bookSelected} disabled={busy || selectedIds.length === 0}
            data-testid="bulk-book-btn"
            className="mt-3 w-full rounded-full border border-white/15 bg-white/[0.06] px-4 py-2.5 text-sm
              text-slate-200 hover:bg-white/[0.1] disabled:opacity-40 transition-colors">
            <span className="inline-flex items-center gap-2">
              <UserCheck className="h-3.5 w-3.5" />
              Book {selectedIds.length || 'selected'} {selectedIds.length === 1 ? 'student' : 'students'} into open times
            </span>
          </button>
          {msg && <p className="mt-3 text-xs font-semibold text-sky-300" data-testid="meetings-msg">{msg}</p>}
        </div>

        {/* Schedule */}
        <div className={`${panel} overflow-hidden`} style={bg}>
          <div className="flex items-center gap-2.5 border-b border-white/[0.07] px-6 py-4">
            <CalendarCheck className="h-4 w-4 text-sky-300" />
            <h3 className="font-outfit text-base font-semibold text-white">My schedule</h3>
            <span className="ml-auto text-[11px] text-slate-300">
              {data.booked_count} booked · {data.open_count} open
            </span>
          </div>
          {loading ? (
            <p className="px-6 py-10 text-center text-sm text-slate-300">Loading…</p>
          ) : data.slots.length === 0 ? (
            <p className="px-6 py-14 text-center text-sm text-slate-300">
              No times published yet. Publish a block on the left and students can claim them themselves.
            </p>
          ) : (
            <div className="max-h-[460px] divide-y divide-white/[0.05] overflow-y-auto">
              {data.slots.map(s => (
                <div key={s.id} className="flex items-center gap-4 px-6 py-3.5" data-testid={`slot-row-${s.id}`}>
                  <div className="w-28 shrink-0">
                    <p className="text-[13px] font-semibold text-white">{s.start_time}</p>
                    <p className="text-[10.5px] text-slate-300">{s.date}</p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] text-slate-200">
                      {s.student_name || <span className="text-slate-300">Open, students can claim</span>}
                    </p>
                    <p className="truncate text-[11px] text-slate-300">
                      {s.topic} · {s.duration_min} min
                      {s.booked_by === 'student' && ' · booked themselves'}
                    </p>
                  </div>
                  {s.student_id
                    ? <span className="shrink-0 rounded-full border border-emerald-400/30 bg-emerald-400/[0.12]
                        px-2.5 py-1 text-[10.5px] font-semibold text-emerald-300">Booked</span>
                    : (
                      <button onClick={() => removeSlot(s.id)} data-testid={`slot-delete-${s.id}`}
                        className="shrink-0 rounded-lg p-1.5 text-slate-300 hover:bg-white/10 hover:text-rose-300 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="flex items-center gap-2 text-[11.5px] text-slate-300">
        <Clock className="h-3.5 w-3.5" />
        Every published block removes the email back-and-forth; every self-booking saves about 6 minutes.
      </p>
    </div>
  );
}
